// frontend/src/utils/contentBlocks.ts
import { ContentBlock } from '../types';

let _counter = 0;
function nextId(): string {
  _counter += 1;
  return `${Date.now().toString(36)}-${_counter.toString(36)}-${Math.random().toString(36).slice(2, 8).padEnd(6, '0')}`;
}

/**
 * 将纯文本切成段落 block。
 * 策略：
 * 1. 优先按空行（连续 \n\n+）切段；
 * 2. 如果没有空行，回退到"句末标点（。！？；）+ 换行"作为段落边界；
 * 3. 段内剩余的 \n 全部合并为空格（处理 LLM 的固定字数硬换行）。
 */
function plainTextToParagraphBlocks(text: string): ContentBlock[] {
  if (!text.trim()) return [];

  // 策略：先标记段落分隔（\n\n+），再把剩余单 \n（硬换行）转空格，最后按标记切段
  // Step 1: 将连续空行替换为段落分隔符 \f
  const withBreaks = text.replace(/\n\n+/g, '\f');
  // Step 2: 剩余单 \n 全部是 LLM 硬换行 → 转空格
  const withSpaces = withBreaks.replace(/\n/g, ' ');
  // Step 3: 按 \f 切段
  const segments = withSpaces.split(/\f/);

  const blocks: ContentBlock[] = [];
  for (const seg of segments) {
    const cleaned = seg.replace(/\s+/g, ' ').trim();
    if (cleaned) {
      blocks.push({ id: nextId(), type: 'paragraph', content: `<p>${cleaned}</p>` });
    }
  }
  return blocks;
}


/**
 * 将 HTML 字符串解析为 ContentBlock 数组。
 * 按顶层 <p>, <h1>-<h6>, <img>, <table> 标签拆分；
 * 匹配之间的纯文本按空行切成多个段落。
 */
export function parseHtmlToBlocks(html: string): ContentBlock[] {
  if (!html || !html.trim()) return [];

  // 如果根本没有块级标签 / img，走纯文本路径
  if (!/<(p|h[1-6]|table|div|img|figure)\b/i.test(html)) {
    return plainTextToParagraphBlocks(html);
  }

  const blockRegex = /<(p|h[1-6]|table|figure)\b[^>]*>[\s\S]*?<\/\1>|<img\b[^>]*\/?>/gi;
  const blocks: ContentBlock[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = blockRegex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      const text = html.slice(lastIndex, match.index);
      blocks.push(...plainTextToParagraphBlocks(text));
    }

    const tag = match[0];
    const tagName = match[1]?.toLowerCase() || (tag.startsWith('<img') ? 'img' : 'p');

    switch (tagName) {
      case 'figure': {
        const figImgMatch = tag.match(/<img\b[^>]*\/?>/i);
        const figSrcMatch = figImgMatch ? figImgMatch[0].match(/src=["']([^"']+)["']/) : null;
        const figCapMatch = tag.match(/<figcaption[^>]*>([\s\S]*?)<\/figcaption>/i);
        blocks.push({
          id: nextId(),
          type: 'image',
          content: figSrcMatch ? figSrcMatch[1] : '',
          meta: {
            queryText: figCapMatch ? figCapMatch[1].replace(/<[^>]*>/g, '').trim() : '',
            figureHtml: tag,
          },
        });
        break;
      }
      case 'img': {
        const srcMatch = tag.match(/src=["']([^"']+)["']/);
        const altMatch = tag.match(/alt=["']([^"']+)["']/);
        blocks.push({
          id: nextId(),
          type: 'image',
          content: srcMatch ? srcMatch[1] : '',
          meta: { queryText: altMatch ? altMatch[1] : '' },
        });
        break;
      }
      case 'table':
        blocks.push({ id: nextId(), type: 'table', content: tag });
        break;
      case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6':
        blocks.push({
          id: nextId(),
          type: 'heading',
          content: tag,
          meta: { level: parseInt(tagName[1]) },
        });
        break;
      default: {
        // Check if paragraph contains only a standalone image
        const innerImgMatch = tag.match(/<img\b[^>]*\/?>/i);
        if (innerImgMatch) {
          const withoutImg = tag.replace(innerImgMatch[0], '');
          const textOnly = withoutImg.replace(/<[^>]*>/g, '').trim();
          if (!textOnly) {
            const imgSrcMatch = innerImgMatch[0].match(/src=["']([^"']+)["']/);
            const imgAltMatch = innerImgMatch[0].match(/alt=["']([^"']+)["']/);
            blocks.push({
              id: nextId(),
              type: 'image',
              content: imgSrcMatch ? imgSrcMatch[1] : '',
              meta: { queryText: imgAltMatch ? imgAltMatch[1] : '' },
            });
            break;
          }
        }
        blocks.push({ id: nextId(), type: 'paragraph', content: tag });
        break;
      }
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < html.length) {
    const text = html.slice(lastIndex);
    blocks.push(...plainTextToParagraphBlocks(text));
  }

  return blocks;
}

/**
 * 将 ContentBlock 数组序列化为 HTML 字符串（用于导出 Word）
 */
export function blocksToHtml(blocks: ContentBlock[]): string {
  return blocks
    .map((b) => {
      if (b.type === 'image') {
        if (b.meta?.figureHtml) {
          return b.meta.figureHtml;
        }
        const alt = (b.meta?.queryText || '').replace(/"/g, '&quot;');
        return `<img src="${b.content}" alt="${alt}" style="max-width:100%;height:auto;" />`;
      }
      return b.content;
    })
    .join('\n');
}

/**
 * 统一入口：兼容 string 和 ContentBlock[]，始终返回 ContentBlock[]
 */
export function contentToBlocks(content: string | ContentBlock[] | undefined): ContentBlock[] {
  if (!content) return [];
  if (typeof content === 'string') {
    return parseHtmlToBlocks(content);
  }
  return [...content];
}

/**
 * 持久化用：直接返回 ContentBlock[]（存储在 outline_data JSONB 中）
 */
export function blocksToContent(blocks: ContentBlock[]): ContentBlock[] {
  return blocks;
}
